import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'

import { format } from 'date-fns'
import { TFunction } from 'i18next'

import { formatDate, formatNumber } from 'app/common/presentation/format'
import { ConnectionName } from 'app/connections/types'
import { DaySessions } from 'app/dashbaord/types'

import { Chart } from 'components'
import { DataEntry } from 'components/Chart'

import { tooltipDateFormat } from '../Dashboard'
import Tile, { TileContentProps } from './Tile'
import styles from './Tile.module.scss'

type ValueEntry = {
  name: ConnectionName
  value: number
}

const determineTreatmentSessionsPerConnection = (sessions: DaySessions[]) =>
  Object.entries(
    sessions.reduce(
      (acc, daySessions) => {
        Object.entries(daySessions.connections).forEach(([connectionName, count]) => {
          if (acc[connectionName as ConnectionName]) {
            acc[connectionName as ConnectionName] += count
          } else {
            acc[connectionName as ConnectionName] = count
          }
        })

        return acc
      },
      {} as Record<ConnectionName, number>,
    ),
  )
    .map(([name, value]) => ({ name: name as ConnectionName, value }) satisfies ValueEntry)
    .sort((a, b) => b.value - a.value)

const determineTreatmentSessionsPerDay = (
  sessions: DaySessions[],
  connectionNames: ConnectionName[],
) =>
  sessions.reduce((acc, daySessions) => {
    acc.push({
      key: daySessions.date,
      values: connectionNames.map((connectionName) => daySessions.connections[connectionName] ?? 0),
    })
    return acc
  }, [] as DataEntry<Date>[])

const colors = ['#1A77F2', '#9DD2B0', '#FABF1D']

const formatTooltipValue = (entry: ValueEntry, index: number, translate: TFunction) => (
  <div className={styles.tooltipValue}>
    <div
      className={styles.indicator}
      style={{ backgroundColor: colors[index % colors.length] }}
    ></div>
    <div className={styles.name}>{translate(`available.${entry.name}.displayName`)}</div>
    <div className={styles.value}>{formatNumber(entry.value)}</div>
  </div>
)

interface TreatmentsProps extends TileContentProps {
  sessions: DaySessions[]
}

const Treatments = ({ days, sessions, translate }: TreatmentsProps) => {
  const { t: st } = useTranslation('connections')

  const treatmentSessionsPerConnection = useMemo(
    () => determineTreatmentSessionsPerConnection(sessions),
    [sessions],
  )
  const usedConnectionNames = useMemo(
    () => treatmentSessionsPerConnection.map((conn) => conn.name),
    [treatmentSessionsPerConnection],
  )
  const treatmentSessionsPerDay = useMemo(
    () => determineTreatmentSessionsPerDay(sessions, usedConnectionNames),
    [sessions, usedConnectionNames],
  )

  return (
    <Tile className={styles.treatments} days={days} tileKey="treatments" translate={translate}>
      <div className={styles.treatments}>
        <Chart.LineChart
          data={treatmentSessionsPerDay}
          formatTooltipLabel={(date) => format(date, tooltipDateFormat)}
          formatTooltipValue={(value, index) =>
            formatTooltipValue({ name: usedConnectionNames[index], value }, index, st)
          }
          formatXTick={formatDate}
          formatYTick={formatNumber}
          reversed
          strokeColors={colors}
          strokeWidth={2}
          yScaleFactor={1.5}
        />
        <div className={styles.chartInfo}>
          <Chart.Tooltip
            className={styles.tooltip}
            formatValue={(value, index) => formatTooltipValue(value, index, st)}
            values={treatmentSessionsPerConnection}
          />
        </div>
      </div>
    </Tile>
  )
}

export default Treatments
